import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2, Search, Send } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Avatar, AvatarImage, AvatarFallback, AvatarPreview } from "../ui/avatar";
import { cn } from "../../lib/utils";

import toastError from "../../errors/toastError";
import api from "../../services/api";

// Encaminhar uma ou mais mensagens para outro contato. O texto é reenviado
// como mensagem nova no atendimento do destinatário — não é o "encaminhado"
// nativo do WhatsApp, que a API não expõe. Quando `messages` (array) é
// passado, encaminha em lote, preservando a ordem cronológica do array.
const ForwardMessageModal = ({ open, onClose, message, messages }) => {
  const [busca, setBusca] = useState("");
  const [contatos, setContatos] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(null);
  const [progresso, setProgresso] = useState(null);

  const mensagens = Array.isArray(messages) && messages.length
    ? messages
    : message
      ? [message]
      : [];

  const procurar = useCallback(async () => {
    if (busca.trim().length < 2) {
      setContatos([]);
      return;
    }

    setBuscando(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: busca.trim() },
      });
      setContatos((data.contacts || []).slice(0, 20));
    } catch (err) {
      toastError(err);
    }
    setBuscando(false);
  }, [busca]);

  useEffect(() => {
    // Espera a digitação parar: uma consulta por tecla castigaria o servidor.
    const t = setTimeout(procurar, 400);

    return () => clearTimeout(t);
  }, [procurar]);

  useEffect(() => {
    if (!open) {
      setBusca("");
      setContatos([]);
    }
  }, [open]);

  // O corpo da mensagem enviada por nós vem prefixado com "*Fulano:* ";
  // encaminhar esse prefixo entregaria uma assinatura que não é do
  // destinatário. Mesma limpeza aplicada a cada item do lote.
  const limparCorpo = (m) =>
    String(m?.body || "").replace(/^\*[^*]+:\*\s*/, "");

  // mediaUrl pode vir como URL absoluta (getter do model) ou caminho
  // relativo salvo no banco. Para o encaminhamento só precisamos do
  // caminho relativo (uuid.ext) que é o que o backend espera.
  const extrairRelativo = (raw) => {
    if (!raw) return null;
    if (typeof raw !== "string") return null;
    const match = raw.match(/\/public\/([^/?#]+)/);
    if (match && match[1]) return match[1];
    if (raw.startsWith("http")) return null;
    return raw.replace(/^\/+/, "");
  };

  const temMidiaEm = (m) => Boolean(m?.mediaUrl) && Boolean(m?.mediaType);

  const resumoLote = (() => {
    const cont = { texto: 0, imagem: 0, audio: 0, video: 0, doc: 0, outro: 0 };
    for (const m of mensagens) {
      const limpo = limparCorpo(m).trim();
      const tipo = String(m?.mediaType || "").toLowerCase();
      const temMidia = temMidiaEm(m);
      if (!temMidia && limpo) {
        cont.texto += 1;
        continue;
      }
      if (tipo === "image") cont.imagem += 1;
      else if (tipo === "audio") cont.audio += 1;
      else if (tipo === "video") cont.video += 1;
      else if (tipo === "application" || tipo === "document") cont.doc += 1;
      else if (temMidia) cont.outro += 1;
      else if (limpo) cont.texto += 1;
      else cont.outro += 1;
    }
    return cont;
  })();

  const [primeiraMsg] = mensagens;
  const primeiraTemMidia = temMidiaEm(primeiraMsg);
  const primeiraRelativo = extrairRelativo(primeiraMsg?.mediaUrl);
  const primeiraTexto = limparCorpo(primeiraMsg);

  const podeEncaminhar = mensagens.some(
    (m) => limparCorpo(m).trim().length > 0 || temMidiaEm(m),
  );

  const rotuloMidia = (tipo, arquivo) => {
    const t = String(tipo || "").toLowerCase();
    const ext = String(arquivo || "")
      .split(".")
      .pop()
      ?.toUpperCase();
    switch (t) {
      case "image":
        return `📷 Imagem${ext ? ` (${ext})` : ""}`;
      case "audio":
        return `🎧 Áudio${ext ? ` (${ext})` : ""}`;
      case "video":
        return `🎥 Vídeo${ext ? ` (${ext})` : ""}`;
      case "application":
      case "document":
        return `📎 Documento${ext ? ` (${ext})` : " (PDF)"}`;
      default:
        return `📎 Arquivo${ext ? ` (${ext})` : ""}`;
    }
  };

  const rotuloResumo = () => {
    const partes = [];
    if (resumoLote.texto) partes.push(`${resumoLote.texto} texto`);
    if (resumoLote.imagem) partes.push(`${resumoLote.imagem} imagem`);
    if (resumoLote.audio) partes.push(`${resumoLote.audio} áudio`);
    if (resumoLote.video) partes.push(`${resumoLote.video} vídeo`);
    if (resumoLote.doc) partes.push(`${resumoLote.doc} documento`);
    if (resumoLote.outro) partes.push(`${resumoLote.outro} outro`);
    return partes.join(", ");
  };

  const encaminhar = async (contato) => {
    if (!podeEncaminhar) return;

    setEnviando(contato.id);
    setProgresso({ atual: 0, total: mensagens.length });
    try {
      // reaproveitarAberto: cai no atendimento que o contato já tem, se
      // houver. Sem isso ficariam dois tickets abertos para a mesma pessoa
      // e o anterior não reabriria mais (ERR_OTHER_OPEN_TICKET).
      const { data } = await api.post("/tickets", {
        contactId: contato.id,
        status: "open",
        userId: null,
        reaproveitarAberto: true,
      });

      // Enviar em SÉRIE (e não em paralelo) para preservar a ordem
      // cronológica das mensagens e evitar sobrecarga no provider.
      for (let i = 0; i < mensagens.length; i++) {
        const m = mensagens[i];
        const body = limparCorpo(m);
        const temMidia = temMidiaEm(m);
        const rel = extrairRelativo(m?.mediaUrl);
        setProgresso({ atual: i + 1, total: mensagens.length });
        await api.post(`/messages/${data.id}`, {
          body,
          mediaUrl: temMidia ? rel : null,
          mediaType: temMidia ? m?.mediaType ?? null : null,
          fromMe: true,
          read: true,
          // Sai marcada como repasse: quem lê a conversa depois precisa saber
          // que aquele texto / mídia não foi criado para aquela pessoa.
          isForwarded: true,
        });
      }

      const qtd = mensagens.length;
      toast.success(
        `${qtd} mensagem${qtd === 1 ? "" : "s"} encaminhada${qtd === 1 ? "" : "s"} para ${contato.name}.`,
      );
      onClose();
    } catch (err) {
      toastError(err);
    }
    setEnviando(null);
    setProgresso(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">
            {mensagens.length > 1
              ? `Encaminhar ${mensagens.length} mensagens`
              : "Encaminhar mensagem"}
          </DialogTitle>
          <div className="mt-1 space-y-1">
            {mensagens.length > 1 ? (
              <>
                <p className="text-xs font-medium text-foreground/90">
                  {rotuloResumo() || `${mensagens.length} itens`}
                </p>
                <p className="text-xs text-muted-foreground">
                  Serão enviadas na mesma ordem da conversa, cada uma marcada
                  como Encaminhada.
                </p>
              </>
            ) : (
              <>
                {primeiraTemMidia && (
                  <p className="text-xs font-medium text-foreground/90">
                    {rotuloMidia(primeiraMsg?.mediaType, primeiraRelativo ?? "")}
                  </p>
                )}
                {primeiraTexto.trim() ? (
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {primeiraTexto}
                  </p>
                ) : primeiraTemMidia ? (
                  <p className="text-xs text-muted-foreground">
                    Será encaminhado com o arquivo original.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Selecione um contato para continuar.
                  </p>
                )}
              </>
            )}
            {progresso && (
              <p className="text-xs text-primary">
                Enviando {progresso.atual} de {progresso.total}…
              </p>
            )}
          </div>
        </DialogHeader>

        <div className="border-b p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              autoFocus
              placeholder="Buscar contato pelo nome ou número..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto p-3">
          {buscando ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : contatos.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {busca.trim().length < 2
                ? "Digite ao menos duas letras para buscar."
                : "Nenhum contato com esse nome."}
            </p>
          ) : (
            contatos.map((contato) => (
              <button
                key={contato.id}
                type="button"
                disabled={!!enviando || !podeEncaminhar}
                onClick={() => encaminhar(contato)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg p-2 text-left",
                  "hover:bg-accent disabled:opacity-60",
                )}
              >
                <AvatarPreview
                  className="h-8 w-8 shrink-0"
                  previewUrl={contato.profilePicUrl}
                  alt={contato.name}
                >
                  <AvatarImage src={contato.profilePicUrl} alt={contato.name} />
                  <AvatarFallback>
                    {(contato.name || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </AvatarPreview>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{contato.name}</p>
                  <p className="truncate text-xs tabular-nums text-muted-foreground">
                    {contato.number || "sem número"}
                  </p>
                </div>

                {enviando === contato.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
            ))
          )}
        </div>

        <DialogFooter className="border-t p-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardMessageModal;
